// The pre-call questions' answers: what the family tapped, as the
// `pre_call_answers` checkpoint stores it.

import { PREP_QUESTIONS } from "./booking-confirmation-copy";
import type { QuoteCheckpoint } from "./api";

type Question = (typeof PREP_QUESTIONS)[number];
export type PrepQuestionId = Question["id"];
type ValueOf<Id extends PrepQuestionId> = Extract<
  Question,
  { id: Id }
>["options"][number]["value"];

export interface PreCallAnswers {
  topics: ValueOf<"topics">[];
  decide: ValueOf<"decide"> | null;
  have: ValueOf<"have">[];
}

export const PRE_CALL_ANSWERS_KIND = "pre_call_answers";

export function emptyAnswers(): PreCallAnswers {
  return { topics: [], decide: null, have: [] };
}

export function isEmpty(answers: PreCallAnswers): boolean {
  return (
    answers.topics.length === 0 &&
    answers.decide === null &&
    answers.have.length === 0
  );
}

function optionValues(id: PrepQuestionId): readonly string[] {
  const question = PREP_QUESTIONS.find((candidate) => candidate.id === id);
  return question ? question.options.map((option) => option.value) : [];
}

/** The selected values of a multi-select question, in the order the
 * options are listed. */
function inListedOrder(id: PrepQuestionId, selected: readonly string[]) {
  return optionValues(id).filter((value) => selected.includes(value));
}

export function isSelected(
  answers: PreCallAnswers,
  id: PrepQuestionId,
  value: string,
): boolean {
  if (id === "decide") return answers.decide === value;
  return (answers[id] as readonly string[]).includes(value);
}

/** Taps an option: a multi-select question toggles the value in and out,
 * the single-select question replaces its value or, when tapped again,
 * clears it. A value the question does not offer changes nothing. */
export function toggleAnswer(
  answers: PreCallAnswers,
  id: PrepQuestionId,
  value: string,
): PreCallAnswers {
  if (!optionValues(id).includes(value)) return answers;
  if (id === "decide") {
    return {
      ...answers,
      decide:
        answers.decide === value ? null : (value as PreCallAnswers["decide"]),
    };
  }
  const current = answers[id] as readonly string[];
  const next = current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
  return { ...answers, [id]: inListedOrder(id, next) } as PreCallAnswers;
}

function knownValues(id: PrepQuestionId, raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return inListedOrder(
    id,
    raw.filter((entry): entry is string => typeof entry === "string"),
  );
}

/** The answers the view's current `pre_call_answers` checkpoint holds;
 * empty when there is none or its shape is not recognized. */
export function fromCheckpoints(
  checkpoints: readonly QuoteCheckpoint[] | null | undefined,
): PreCallAnswers {
  const row = checkpoints?.find(
    (checkpoint) => checkpoint.kind === PRE_CALL_ANSWERS_KIND,
  );
  const resolution = row?.resolution;
  if (!resolution || typeof resolution !== "object") return emptyAnswers();
  const raw = resolution as Record<string, unknown>;
  const decide = optionValues("decide").find((value) => value === raw.decide);
  return {
    topics: knownValues("topics", raw.topics) as PreCallAnswers["topics"],
    decide: (decide ?? null) as PreCallAnswers["decide"],
    have: knownValues("have", raw.have) as PreCallAnswers["have"],
  };
}

export function sameAnswers(a: PreCallAnswers, b: PreCallAnswers): boolean {
  return (
    a.decide === b.decide &&
    a.topics.length === b.topics.length &&
    a.topics.every((value, index) => value === b.topics[index]) &&
    a.have.length === b.have.length &&
    a.have.every((value, index) => value === b.have[index])
  );
}
